import { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Spinner,
  Text,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import BarChart from "components/charts/BarChart";
import Menu from "components/menu/MainMenu";
import { barChartOptionsConsumption } from "variables/charts";
import { useClients } from "hooks/ClientsHook";
import { useQuery } from "react-query";

interface DefaultProps {
  id: number;
  total: (value: number) => void;
}

export default function NumerariosCardsCliente(
  props: DefaultProps & { [key: string]: any }
) {
  const { id, ...rest } = props;
  const [filter, setFilter] = useState<number>(7);
  const theme = useColorMode();
  const themeSelect = theme.colorMode;

  const { findCashTypesGraphics } = useClients();
  const { data, isLoading, error } = useQuery(
    ["findCashTypesGraphics", filter, id],
    () =>
      findCashTypesGraphics({
        dias: filter,
        idClient: id,
      })
  );

  useEffect(() => {
    if (
      data &&
      data?.NUMERARIOS &&
      data?.NUMERARIOS.VALORES &&
      Array.isArray(data?.NUMERARIOS.VALORES)
    ) {
      const valores = data?.NUMERARIOS.VALORES.map((valor: string) =>
        parseFloat(valor)
      );
      const totalValores = valores.reduce(
        (acc: number, valor: number) => acc + valor,
        0
      );
      props.total(totalValores);
    }
  }, [data, props]);

  // Chakra Color Mode
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorGraphic = useColorModeValue("gray", "white");

  return (
    <Card
      gridColumnStart={{ base: 1, md: 2, lg: 2 }}
      gridColumnEnd={{ base: 1, md: 2, lg: 4 }}
      alignItems="center"
      flexDirection="column"
      w="100%"
      key={data?.NUMERARIOS?.NOMES?.[0] + themeSelect}
      {...rest}
    >
      <Flex align="center" w="100%" px="15px" py="10px">
        <Text
          me="auto"
          color={textColor}
          fontSize="xl"
          fontWeight="700"
          lineHeight="100%"
        >
          Númerarios
        </Text>
        <Menu onFilterSelected={(data: any) => setFilter(data)} />
      </Flex>

      {isLoading ? (
        <Box
          display="flex"
          alignItems="center"
          padding={30}
          justifyContent="center"
        >
          <Spinner width={100} height={100} />
        </Box>
      ) : error ? (
        <Text fontSize={21} fontWeight={800} color="red.500" textAlign="center">
          {String(error)}
        </Text>
      ) : !data || data?.NUMERARIOS.VALORES.length === 0 ? (
        <Text fontSize={21} fontWeight={800} textAlign="center">
          Sem vendas registradas
        </Text>
      ) : (
        <Box h="100%" mt="auto" w="100%">
          <BarChart
            chartData={[
              {
                name: "Valores",
                data: data?.NUMERARIOS.VALORES.map((valor: string) =>
                  parseFloat(valor)
                ) ,
              },
            ]}
            chartOptions={{
              ...barChartOptionsConsumption,
              series: {
                data: [data?.NUMERARIOS.NOMES] ?? [],
              },
              theme: {
                mode: themeSelect,
              },
              dataLabels: {
                enabled: true,
                formatter: (value: number) =>
                  value.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }),
                style: {
                  fontSize: "14px",
                  fontFamily: "Helvetica, Arial, sans-serif",
                  colors: [textColorGraphic] ?? [],
                },
              },
              xaxis: {
                categories: data?.NUMERARIOS.NOMES,
                labels: {
                  formatter: (value: number) => {
                    if (value >= 1_000_000) {
                      return value / 1_000_000 + "M";
                    } else if (value >= 1_000) {
                      return value / 1_000 + "K";
                    } else {
                      return value;
                    }
                  },
                  style: {
                    colors: "#A3AED0",
                    fontSize: "12px",
                    fontWeight: "500",
                    padding: "4px",
                  },
                  axisBorder: {
                    show: false,
                  },
                  axisTicks: {
                    show: false,
                  },
                },
              },
            }}
          />
        </Box>
      )}
    </Card>
  );
}
